export const MY_ORGANIZATIONS_GROUPING = 'My organizations';
const USER_ORGANIZATIONS_GROUPING = 'User organizations';
/**
 * The backend's single user-org branch (#1000): user orgs hang off
 * `Users/<email>/<name>`. "My organizations" / "User organizations" are now a
 * UI relabeling. The two legacy names are still recognized so the UI works
 * against a backend that hasn't shipped the `Users` branch yet.
 */
export const USERS_GROUPING = 'Users';

/**
 * Whether an id/name is a synthetic user-org grouping node — the `Users` root,
 * a `Users/<email>` owner bucket, or a legacy `My`/`User organizations` bucket
 * — i.e. a structural node that is not itself a selectable organization.
 */
export const isUserOrgGroupingNode = (
  id?: string | null,
  name?: string | null
): boolean =>
  id === USERS_GROUPING ||
  id === MY_ORGANIZATIONS_GROUPING ||
  id === USER_ORGANIZATIONS_GROUPING ||
  name === USERS_GROUPING ||
  name === MY_ORGANIZATIONS_GROUPING ||
  name === USER_ORGANIZATIONS_GROUPING ||
  isUsersOwnerBucketId(id);

/**
 * A `Users/<email>` owner-bucket id of the form `Users/<segment>` — exactly two
 * slash-delimited segments. (A leaf org is `Users/<email>/<name>` — three or more.)
 */
const isUsersOwnerBucketId = (id?: string | null): boolean =>
  Boolean(id?.startsWith(`${USERS_GROUPING}/`)) && id.split('/').length === 2;

/** Whether the node is the `Users/<email>` owner bucket for {@link email}. */
export const isOwnUserOrgBucket = (
  org: { id?: string | null; name?: string | null } | null | undefined,
  email: string | null | undefined
): boolean =>
  Boolean(email) && isUsersOwnerBucketId(org?.id) && org.name === email;

export const GLOBAL_ORGANIZATION_ID = 'ε';
export const GLOBAL_ORGANIZATION_DISPLAY_NAME = 'Global';
export const MISSING_ORGANIZATION_DISPLAY_NAME = 'Organization unavailable';

export const isGlobalOrganization = (
  organizationId: string | null | undefined
): boolean => organizationId === GLOBAL_ORGANIZATION_ID;

/**
 * Returns the display name for an organization, aliasing ε → Global.
 * Accepts either an object with id/name or just a name string.
 */
export const getOrganizationDisplayName = (
  org: { id?: string; name?: string } | string | null | undefined
): string => {
  if (!org) {
    return '';
  }
  if (typeof org === 'string') {
    return org === GLOBAL_ORGANIZATION_ID
      ? GLOBAL_ORGANIZATION_DISPLAY_NAME
      : org;
  }
  if (
    org.id === GLOBAL_ORGANIZATION_ID ||
    org.name === GLOBAL_ORGANIZATION_ID
  ) {
    return GLOBAL_ORGANIZATION_DISPLAY_NAME;
  }
  // Relabel the backend's `Users` grouping to the familiar "User organizations".
  if (org.id === USERS_GROUPING || org.name === USERS_GROUPING) {
    return USER_ORGANIZATIONS_GROUPING;
  }
  return org.name ?? '';
};

/**
 * Determines if an organization is a user organization.
 * V1: computed from the user field (non-null = user organization)
 * V2: computed from the parent hierarchy (parent is "My organizations")
 */
export const isUserOrganization = <
  T extends { user?: { email: string } | null } | Record<string, unknown>
>(
  organization: T | null | undefined
): boolean => {
  if (!organization) {
    return false;
  }
  if ('user' in organization && organization.user != null) {
    return true;
  }
  if ('parents' in organization && Array.isArray(organization.parents)) {
    return (organization.parents as Array<{ id?: string; name?: string }>).some(
      (parent) => isUserOrgGroupingNode(parent?.id, parent?.name)
    );
  }
  return false;
};
